'use client'

import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { AlertTriangle, ChevronDown, Lock } from 'lucide-react'
import type { ActionItem, Insight, ToolKey, ToolState } from '@/types/radar'
import { sortActions } from '@/lib/radar/action-priority'
import { TOOLS } from '@/types/radar'
import { TOOL_GROUPS } from '@/lib/radar/scoring'
import { hasUsableScore, passingCount } from '@/lib/radar/tool-scores'
import { CATEGORIES } from '@/lib/radar/categories'
import { Button } from '@/components/platform/ui'
import { ScoreInterpretation } from './ScoreInterpretation'
import { buildVerdict } from '@/lib/radar/verdict'
import { DashboardHero } from './DashboardHero'
import { SignalDetailBand } from './SignalProfile'
import { MetricBand, type Metric } from './MetricBand'
import { IssuesSummaryCard } from './IssuesSummaryCard'
import { ToolResultsTable } from './ToolResultsTable'
import { InsightsTabs } from './InsightsTabs'
import { PriorityRoadmap } from './PriorityRoadmap'
import { ActionPlan } from './ActionPlan'
import { WatchDomainCTA } from './WatchDomainCTA'
import {
  DashboardFilterBar,
  EMPTY_FILTERS,
  type DashboardFilters,
} from './DashboardFilterBar'
import { InstrumentLabel, Panel } from './ui'

/**
 * The Overview tab — composition only.
 *
 * Every figure on this screen is derived by a module under lib/radar and
 * rendered by a child component. This file wires them together and owns
 * exactly one piece of state: the persisted tool filter. It performs no
 * scoring arithmetic.
 *
 * Rewritten 2026-07-26 against public/Radar Dashboard.html. The previous
 * version carried its own donut / bar / gauge chart implementations plus a
 * 12-column card grid inline; those are gone in favour of the hero, the five
 * category cards, and the tool table.
 */

const FILTERS_STORAGE_KEY = 'radar:dashboard:filters'

export function DashboardOverview({
  tools,
  isRunning,
  unifiedScore,
  unifiedGrade,
  hasUnifiedScore = true,
  scoredCount,
  withheldTools = [],
  doneCount,
  totalCount,
  insights,
  onToolClick,
  onRetry,
  previousScore,
  previousIssueCount,
  previousScanDate,
  previousToolScores,
  actions,
  onNavigateToActions,
  totalElapsed,
  showActionPlan,
  onDismissActionPlan,
  activeDomain,
  savedRunsCount,
  userTier,
  onUpgradeClick,
  strategistSlot,
}: {
  tools: Record<ToolKey, ToolState>
  isRunning: boolean
  /** 0 is a placeholder when hasUnifiedScore is false — never rendered then. */
  unifiedScore: number
  unifiedGrade: string
  /** False when every completed tool withheld its grade. */
  hasUnifiedScore?: boolean
  /** Tools that produced a usable score — the denominator behind the number. */
  scoredCount?: number
  /** Completed tools that declined to grade. */
  withheldTools?: ToolKey[]
  doneCount: number
  totalCount: number
  insights: Insight[]
  onToolClick: (key: ToolKey) => void
  onRetry: (key: ToolKey) => void
  previousScore?: number | null
  previousIssueCount?: number | null
  previousScanDate?: number | null
  previousToolScores?: Record<string, { score: number; grade: string }> | null
  actions: ActionItem[]
  onNavigateToActions: (filter?: string) => void
  totalElapsed?: number | null
  showActionPlan: boolean
  onDismissActionPlan: () => void
  activeDomain?: string | null
  savedRunsCount?: number
  userTier?: 'free' | 'paid'
  onUpgradeClick?: () => void
  strategistSlot?: ReactNode
}) {
  // ── Filter state (persisted) ────────────────────────────────────────
  const [filters, setFilters] = useState<DashboardFilters>(EMPTY_FILTERS)
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const raw = window.localStorage.getItem(FILTERS_STORAGE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw) as Partial<DashboardFilters>
      if (parsed.group && parsed.grade !== undefined) {
        setFilters({
          group: parsed.group as DashboardFilters['group'],
          grade: parsed.grade ?? null,
          category: parsed.category ?? null,
        })
      }
    } catch {
      /* corrupt storage — ignore */
    }
  }, [])
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters))
    } catch {
      /* storage full / private mode — ignore */
    }
  }, [filters])

  const visibleKeys = useMemo(() => {
    const categoryKeys = filters.category
      ? new Set<string>(
          CATEGORIES.find(c => c.key === filters.category)?.keys ?? []
        )
      : null
    return new Set(
      TOOLS.filter(t => {
        if (filters.group !== 'all') {
          const allowed = TOOL_GROUPS[filters.group].keys as readonly string[]
          if (!allowed.includes(t.key)) return false
        }
        if (categoryKeys && !categoryKeys.has(t.key)) return false
        if (filters.grade) {
          const state = tools[t.key]
          return hasUsableScore(state) && state.data?.grade === filters.grade
        }
        return true
      }).map(t => t.key)
    )
  }, [filters, tools])

  const filterLabel = useMemo(() => {
    const parts: string[] = []
    if (filters.category) {
      parts.push(
        CATEGORIES.find(c => c.key === filters.category)?.label ??
          filters.category
      )
    }
    if (filters.group !== 'all') parts.push(TOOL_GROUPS[filters.group].label)
    if (filters.grade) parts.push(`Grade ${filters.grade}`)
    if (parts.length === 0) return null
    return `${parts.join(' · ')} · ${visibleKeys.size} ${visibleKeys.size === 1 ? 'tool' : 'tools'}`
  }, [filters, visibleKeys])

  // ── Derived display values ──────────────────────────────────────────
  const issueBreakdown = useMemo(() => {
    let errors = 0
    let warnings = 0
    let notices = 0
    for (const ins of insights) {
      if (ins.type === 'bad') errors++
      else if (ins.type === 'conflict') warnings++
      else notices++
    }
    return { errors, warnings, notices }
  }, [insights])

  const passCount = useMemo(() => passingCount(tools), [tools])

  const conflicts = useMemo(
    () => insights.filter(i => i.type === 'conflict'),
    [insights]
  )

  // `actions`, NOT an insight tally. The sidebar badge (page.tsx ->
  // buildNavModel actionCount) and ActionPlan both count this list, so a tile
  // labelled "Recommendations" has to count the same thing or the dashboard
  // contradicts itself. The earlier errors+notices sum excluded conflicts
  // while the detail line printed them, so a conflict-only audit rendered
  // "0 open · 1 conflict".
  const quickWins = useMemo(
    () => actions.filter(a => a.effort === 'low').length,
    [actions]
  )

  const failedCount = useMemo(
    () => Object.values(tools).filter(s => s?.status === 'error').length,
    [tools]
  )
  const lockedCount = useMemo(
    () => Object.values(tools).filter(s => s?.status === 'locked').length,
    [tools]
  )

  // Every `detail` line below is derived from this same audit. Nothing here
  // is a benchmark, an estimate, or a comparison to other domains — the
  // radar-v2 mock fills these lines with figures this product never measures.
  const metrics: Metric[] = [
    {
      label: 'Tools completed',
      value: `${doneCount}/${totalCount}`,
      detail:
        joinDetail([
          failedCount > 0 ? `${failedCount} failed` : null,
          lockedCount > 0 ? `${lockedCount} locked` : null,
          withheldTools.length > 0
            ? `${withheldTools.length} withheld grade${withheldTools.length === 1 ? '' : 's'}`
            : null,
        ]) ?? (doneCount === totalCount ? 'All sources responded' : undefined),
    },
    {
      label: 'Checks',
      value: scoredCount ? passCount : '--',
      unit: scoredCount ? 'passed' : undefined,
      detail: scoredCount
        ? `of ${scoredCount} gradeable ${scoredCount === 1 ? 'tool' : 'tools'}`
        : 'No gradeable tools yet',
    },
    {
      label: 'Recommendations',
      value: actions.length,
      unit: 'open',
      // page.tsx gates generateActions on !isRunning, so `actions` is empty
      // for the whole scan. Without this the tile reads "0 open · Nothing
      // outstanding" mid-run — a finding reported before it was computed.
      pending: isRunning,
      color: actions.length > 0 ? 'var(--cta)' : undefined,
      // Quick wins are derived from `effort`, which generateActions sets.
      // `impact` is deliberately NOT surfaced here: it is a hand-authored
      // severity, not a measurement, and a metric tile would read it as one.
      detail:
        joinDetail([
          quickWins > 0
            ? `${quickWins} quick ${quickWins === 1 ? 'win' : 'wins'}`
            : null,
        ]) ?? (actions.length === 0 ? 'Nothing outstanding' : undefined),
    },
    {
      label: 'Last audit',
      value:
        totalElapsed != null && totalElapsed > 0
          ? `${totalElapsed.toFixed(1)}s`
          : previousScanDate
            ? relativeDay(previousScanDate)
            : '--',
      detail: activeDomain ?? undefined,
    },
  ]

  // The action the "Next best move" slot reads. Taken from the SORTED list,
  // never `actions[0]` — a persisted snapshot can carry rows in payload order.
  const nextAction = useMemo(() => sortActions(actions)[0] ?? null, [actions])

  // ONE verdict per render, built from the audit and shared: the headline and
  // description go above the fold here, the impact line goes to
  // ScoreInterpretation below. Neither component authors copy of its own.
  const verdict = buildVerdict({
    score: hasUnifiedScore ? unifiedScore : null,
    tools,
    errorCount: issueBreakdown.errors,
    warningCount: issueBreakdown.warnings,
  })
  const interpretation = {
    headline: verdict.headline,
    description: verdict.description,
  }

  return (
    <div className='flex flex-col gap-5'>
      <DashboardHero
        score={hasUnifiedScore ? unifiedScore : null}
        grade={unifiedGrade}
        scoredCount={scoredCount ?? 0}
        withheldCount={withheldTools.length}
        interpretation={doneCount > 0 ? interpretation : null}
        previousScore={previousScore}
        activeDomain={activeDomain}
        tools={tools}
        nextAction={isRunning ? null : nextAction}
        onReviewAction={() => onNavigateToActions()}
        isRunning={isRunning}
      />

      {/* Pillar detail, straight under the chart it belongs to. */}
      {doneCount > 0 && (
        <SignalDetailBand
          tools={tools}
          activeCategory={filters.category}
          onCategoryClick={key =>
            setFilters(f => ({
              ...f,
              category: f.category === key ? null : key,
            }))
          }
        />
      )}

      <MetricBand metrics={metrics} />

      {/* Open actions banner */}
      {!isRunning && actions.length > 0 && showActionPlan && (
        <ActionPlan
          actions={actions}
          onNavigateToActions={() => onNavigateToActions()}
          onDismiss={onDismissActionPlan}
        />
      )}

      {/* Priority roadmap (paid only; withheld unless the parity gate passes).
          Sits with the actions banner rather than below the fold: the two are
          one "priority queue" block in the radar-v2 arrangement, read straight
          after the score. Free tier renders nothing here, so the banner simply
          closes the block. */}
      {!isRunning && doneCount > 0 && (
        <PriorityRoadmap tools={tools} actions={actions} userTier={userTier} />
      )}

      {/* The five category cards used to sit here. They were removed once the
          hero gained the signal profile: both are the same re-cut of the same
          tool scores, one screen apart. Its job as the category filter control
          moved to the profile rows. `CategoryGrid` itself is now unrendered —
          the homepage RadarDemo was realigned to this same arrangement, so the
          component survives only as an export. */}

      {/* Strategist + issues */}
      {(strategistSlot || doneCount > 0) && (
        <div className='grid grid-cols-1 gap-3 lg:grid-cols-3'>
          {strategistSlot && (
            <div className='lg:col-span-2'>{strategistSlot}</div>
          )}
          {doneCount > 0 && (
            <div className={strategistSlot ? '' : 'lg:col-span-3'}>
              <IssuesSummaryCard
                errors={issueBreakdown.errors}
                warnings={issueBreakdown.warnings}
                notices={issueBreakdown.notices}
                previousIssueCount={previousIssueCount}
                onSelect={severity => onNavigateToActions(severity)}
              />
            </div>
          )}
        </div>
      )}

      {/* Cross-tool conflicts — Radar's most differentiated output, kept as a
          dedicated callout rather than only a row in the insights list. */}
      {!isRunning && conflicts.length > 0 && (
        <Panel
          tone='action'
          className='flex items-start gap-4 p-5 sm:px-7'
          as='div'
        >
          <AlertTriangle
            size={16}
            aria-hidden='true'
            className='mt-0.5 shrink-0'
            style={{ color: 'var(--warning-text)' }}
          />
          <div className='flex min-w-0 flex-col gap-2'>
            <InstrumentLabel tone='secondary'>
              Cross-tool conflicts · {conflicts.length}
            </InstrumentLabel>
            {conflicts.slice(0, 3).map((c, i) => (
              <span
                key={i}
                className='block max-w-[68ch] text-[13px] leading-relaxed text-muted-foreground'
              >
                <span className='font-semibold text-foreground'>
                  {c.title}:
                </span>{' '}
                {c.message}
              </span>
            ))}
            {conflicts.length > 3 && (
              <button
                type='button'
                onClick={() => onNavigateToActions('warnings')}
                className='self-start text-xs text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card'
              >
                +{conflicts.length - 3} more conflicts
              </button>
            )}
          </div>
        </Panel>
      )}

      {/* Free-tier upsell */}
      {!isRunning && doneCount > 0 && userTier === 'free' && onUpgradeClick && (
        <Panel
          as='div'
          className='flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:px-7'
        >
          <Lock
            size={16}
            aria-hidden='true'
            className='shrink-0'
            style={{ color: 'var(--purple)' }}
          />
          <div className='flex min-w-0 flex-1 flex-col gap-1'>
            <span className='block text-sm font-semibold'>
              6 more tools locked — unlock for the full picture
            </span>
            <span className='block max-w-[68ch] text-[13px] leading-relaxed text-muted-foreground'>
              Citation Tracker (4 LLMs + Grok), Reddit Monitor, Hallucination
              Detection, Prompt SOV, Source Influence Map, Answer Engine Tester.
              From $5 per audit.
            </span>
          </div>
          <Button variant='primary' size='sm' onClick={onUpgradeClick}>
            Unlock
          </Button>
        </Panel>
      )}

      {/* Watch this domain — the enrollment form the sidebar's "Watching"
          summary refers to. Collapsed so it never competes with results. */}
      {!isRunning && doneCount > 0 && activeDomain && (
        <Panel as='details' className='group/watch'>
          <summary className='flex cursor-pointer select-none items-center justify-between gap-3 px-5 py-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset sm:px-7'>
            <InstrumentLabel tone='secondary'>
              Watch — weekly re-scan + email when your score moves
            </InstrumentLabel>
            <ChevronDown
              size={14}
              aria-hidden='true'
              className='shrink-0 -rotate-90 text-muted-foreground transition-transform group-open/watch:rotate-0'
            />
          </summary>
          <div className='relative border-t border-hairline'>
            <WatchDomainCTA
              domain={activeDomain}
              unifiedScore={unifiedScore}
              unifiedGrade={unifiedGrade}
              toolScores={Object.entries(tools)
                .filter(([, s]) => hasUsableScore(s))
                .map(([key, s]) => ({
                  name: TOOLS.find(t => t.key === key)?.name || key,
                  score: s.data.overallScore as number,
                  grade: s.data?.grade ?? 'F',
                }))}
              // Bucket from the SORTED index, never from `a.priority`.
              // generateActions keeps that field in step with canonical
              // order, but a persisted snapshot or API payload can carry a
              // stale value, which would label a correctly-sorted list wrong.
              topActions={sortActions(actions)
                .slice(0, 5)
                .map((a, i) => ({
                  title: a.title,
                  priority: i < 3 ? 'high' : i < 6 ? 'medium' : 'low',
                }))}
            />
            {userTier === 'free' && (
              <div className='absolute inset-0 flex items-center justify-center bg-black/78 backdrop-blur-[2px]'>
                <div className='px-6 text-center'>
                  <span className='block mb-1 text-sm font-semibold text-white'>
                    Weekly re-scan is a paid feature
                  </span>
                  <span className='block mb-3 text-xs text-white/70'>
                    Included with any paid plan. From $5.
                  </span>
                  <Button
                    variant='primary'
                    size='sm'
                    onClick={() => onUpgradeClick?.()}
                  >
                    Unlock
                  </Button>
                </div>
              </div>
            )}
          </div>
        </Panel>
      )}

      {/* Impact framing + movement against the previous run. The headline and
          description this panel used to open with now live in ScoreHero. */}
      {!isRunning && doneCount > 0 && hasUnifiedScore && (
        <ScoreInterpretation
          unifiedScore={unifiedScore}
          unifiedGrade={unifiedGrade}
          doneCount={doneCount}
          errorCount={issueBreakdown.errors}
          warningCount={issueBreakdown.warnings}
          previousScore={previousScore}
          previousIssueCount={previousIssueCount}
          previousScanDate={previousScanDate}
          previousToolScores={previousToolScores}
          tools={tools}
        />
      )}

      {/* Tool results */}
      {(doneCount > 0 || isRunning) && (
        <>
          <DashboardFilterBar
            filters={filters}
            onFiltersChange={setFilters}
            matchedCount={visibleKeys.size}
            totalCount={TOOLS.length}
          />
          <ToolResultsTable
            tools={tools}
            visibleKeys={visibleKeys}
            filterLabel={filterLabel}
            onClearFilter={() => setFilters(EMPTY_FILTERS)}
            onToolClick={onToolClick}
            onRetry={onRetry}
          />
        </>
      )}

      {/* Cross-tool insights + ranked issues */}
      {!isRunning && <InsightsTabs insights={insights} actions={actions} />}

      {(savedRunsCount ?? 0) > 0 && (
        <span className='block text-xs text-instrument'>
          {savedRunsCount} saved {savedRunsCount === 1 ? 'run' : 'runs'} in
          history.
        </span>
      )}
    </div>
  )
}

/**
 * Joins the parts of a metric's detail line, or returns undefined when every
 * part is absent — so the caller can fall back rather than render an empty
 * string or a stray separator.
 */
function joinDetail(parts: (string | null)[]): string | undefined {
  const present = parts.filter((p): p is string => Boolean(p))
  return present.length > 0 ? present.join(' · ') : undefined
}

function relativeDay(timestamp: number): string {
  const days = Math.floor((Date.now() - timestamp) / 86_400_000)
  if (days <= 0) return 'today'
  if (days === 1) return '1d ago'
  if (days < 30) return `${days}d ago`
  return new Date(timestamp).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  })
}
